import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, X } from "lucide-react";

const DISMISSED_KEY = "browser-translate-hint-dismissed";
const LANG_NAMES = { en: "english", es: "spanish" };

function BrowserTranslateHint() {
    const { t, i18n } = useTranslation();
    const [visible, setVisible] = useState(false);
    const browserLang = (navigator.language || "").split("-")[0];

    useEffect(() => {
        if (localStorage.getItem(DISMISSED_KEY)) return;
        const appLang = (i18n.language || "en").split("-")[0];
        const mismatch = browserLang && appLang && browserLang !== appLang;
        setVisible(!!mismatch);
    }, [i18n.language]);

    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(dismiss, 90000);
        return () => clearTimeout(timer);
    }, [visible]);

    const switchToBrowserLang = () => {
        i18n.changeLanguage(browserLang);
        localStorage.setItem("i18nextLng", browserLang);
        dismiss();
    };

    const dismiss = () => {
        localStorage.setItem(DISMISSED_KEY, "true");
        setVisible(false);
    };

    if (!visible) return null;

    // Render text in the browser's language so the user can read it
    const bt = (key, options) => t(key, { ...options, lng: browserLang });
    const langName = bt(`languageSwitcher.${LANG_NAMES[browserLang] || "english"}`);

    return (
        <div className="fixed top-4 right-4 z-50 max-w-sm animate-in slide-in-from-top-4 fade-in duration-500">
            <div className="bg-white border border-blue-200 rounded-xl shadow-lg p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Globe className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                            {bt("browserTranslateHint.title")}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            {bt("browserTranslateHint.description")}
                        </p>
                        <button
                            onClick={switchToBrowserLang}
                            className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        >
                            {bt("browserTranslateHint.switchButton", { language: langName })}
                        </button>
                    </div>
                    <button
                        onClick={dismiss}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BrowserTranslateHint;
